<template>
  <div>
    <b-overlay :show="busy" rounded="sm">
      <div v-if="busy" class="mt-5"></div>
      <div v-if="totalProgressPct > 0" class="container-fluid">
        <div class="row">
          <div class="col-2">
            <h6 class="mb-0">{{ $t('Create.uploading') }}</h6>
            <small>({{ $t('BasicUploader.total-bytes') }}: {{ totalUploadedBytes }})</small>
          </div>
          <div class="col-9">
            <b-progress height="2.6rem" :max="100" :value="totalProgressPct" show-progress animated></b-progress>
          </div>
        </div>
      </div>

      <vue-clip
        v-if="!busy"
        :options="options"
        class="uploader"
        :on-sending="onSending"
        :on-total-progress="onTotalProgress"
        :on-queue-complete="onQueueComplete"
      >
        <template slot="clip-uploader-action" slot-scope="props">
          <div class="uploader-action" :class="{ dragging: props.dragging }">
            <div class="dz-message">
              <h5>{{ $t('Create.click-or-drag-and-drop-files-here-upload') }}</h5>
              <span>
                {{ $t('Create.video-files-documents-or-folders') }}
              </span>
            </div>
          </div>
        </template>

        <template slot="clip-uploader-body" slot-scope="props">
          <div class="uploader-files">
            <div v-for="(file, i) in props.files.slice().reverse()" :key="i" class="uploader-file">
              <div class="file-avatar">
                <img :src="file.dataUrl" />
              </div>
              <div class="file-details">
                <div class="file-name">{{ file.name }} {{ file.status }}</div>
                <div v-if="file.status != 'error' && file.status !== 'success'" class="file-progress">
                  <span :style="{ width: file.progress + '%' }" class="progress-indicator"></span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </vue-clip>
    </b-overlay>
  </div>
</template>
<script>
import NetworkMediaService from '@/services/NetworkMediaService.js';
import Vue from 'vue';
import VueClip from 'vue-clip';

// install the plugin (but not globally)
Vue.use(VueClip);

export default {
  name: 'network-media-create',
  props: {
    networks: {
      type: [Array, Object],
      default: null,
    },
    collectionId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      // status / UI fields
      busy: true,
      totalProgressPct: 0,
      totalUploadedBytes: 0,
      files: [],
      s3Params: {},
      mediaForm: {},
      // once the files become media items, they are stored here
      registeredFiles: [],
      options: {
        maxFilesize: 4000, // set to 2GB, but we define an accept function will checks this.maxFilesize (above)
        maxFiles: {
          limit: 2000,
          message: this.$t('Create.you-can-only-upload-a-max-of-2000-files'),
        },
        autoProcessQueue: true,
      }, // uploader options
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    /**
     * Initialize the uploader, get the s3 upload params
     *
     */
    init() {
      this.busy = true;
      this.$http
        .post('s3/upload-params')
        .then(resp => {
          this.setupUploadForm(resp.data);
          // upload seutp is done!
          this.busy = false;
        })
        .catch(err => {
          this.$bvModal.msgBoxOk(this.$t('CreateMediaWithoutCollection.s3-upload-initialization-failed-please-check-console-for-details-or-refresh-the-page-to-try-again'));
          console.log(this.$t('CreateMediaWithoutCollection.get-s3-upload-params-failed'), err);
          this.busy = false;
        });
    },
    setupUploadForm(s3) {
      // see option documentation here: https://github.com/thetutlage/vue-clip
      this.options.url = s3.formAttributes.action;
      this.options.accept = (file, done) => {
        if (file.size > this.maxFilesize * 1024 * 1024) {
          let msg = this.$t('Create.file-size-cannot-be-higher-than') + this.maxFilesize + 'MB';
          this.$bvModal.msgBoxOk(msg);

          done(this.$t('Create.file-must-be-smaller-than-1mb')); // for some reason this does not work, so we have to use the message above
          return;
        }
        done();
      };
      this.s3Params = s3;
    },
    onTotalProgress(progress, totalUploadedBytes) {
      this.totalProgressPct = progress;
      this.totalUploadedBytes = totalUploadedBytes;
    },
    onQueueComplete() {
      this.processFiles();
    },
    // Files are uploaded, this method will now create the necessary network media items using our API
    processFiles() {
      if (this.files.length == 0) {
        // most likely the file is too big
        let msg = this.$t('network.error.no_files_uploaded');
        this.$bvModal.msgBoxOk(msg);
      } else {
        // lets create a request to the backend so that a media item is created for this.
        // Once created, we will then redirec the user to the edit form for the media
        let slugs = this.networks.map(m => m.slug);

        NetworkMediaService.registerFiles(slugs, this.files, this.s3Params.uploadPathPrefix, { id: this.collectionId })
          .then(resp => {
            let media = [];
            this.registeredFiles = resp.data.files;
            this.registeredFiles.forEach(file => media.push(file.networkMedia));
            this.$emit('media-added-to-collection', media);
          })
          .catch(resp => {
            this.$bvModal.msgBoxOk(this.$t('Create.error-occurred-on-the-server-please-see-your-browser-console-and-contact-resonance-for-help'));
            console.log('resp', resp);
          });
      }
    },
    // Before sending a file, attach the s3 required parameters
    onSending(file, xhr, formData) {
      var fullPath;
      // if a full path is set
      if ('fullPath' in file._file && file._file.fullPath) {
        fullPath = file._file.fullPath;
      } else {
        // probably a single file upload, no need for the path info
        fullPath = file._file.name;
      }
      var properPath = this.s3Params.uploadPathPrefix + '/' + fullPath;

      // update formData before the file is uploaded
      for (let key in this.s3Params.formInputs) {
        var value = this.s3Params.formInputs[key];
        if (key == 'key') value = properPath; // use the proper path calculated (in case its a full folder upload)

        formData.append(key, value);
      }
      this.files.push({
        name: file.name,
        size: file._file.size,
        fullPath: properPath,
      });
    },
  },
};
</script>
<style>
.uploader {
  /*width: 400px;
  height: 550px;*/
  border-radius: 6px;
  background: #fff;
}
.uploader * {
  box-sizing: border-box;
}
.uploader-action {
  padding: 20px;
  background: #f1f5ff;
  cursor: pointer;
  transition: background 300ms ease;
}
.uploader-action .dz-message {
  color: #666;
  text-align: center;
  padding: 20px 40px;
  border: 3px dashed #dfe8fe;
  border-radius: 4px;
  font-size: 16px;
}
.uploader-files {
  flex: 1;
  padding: 40px;
}
.file-progress {
  background: #e3ebfa;
  border-radius: 8px;
  height: 4px;
  width: 80%;
}
.progress-indicator {
  display: block;
  background: #00d28a;
  border-radius: 8px;
  height: 4px;
}
.file-size {
  font-weight: 600;
  color: #bebfc1;
}
.file-status {
  font-size: 12px;
  text-transform: uppercase;
  margin-left: 5px;
}
@keyframes slideUpIn {
  0% {
    opacity: 0;
    transform: translateY(10%);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}
.uploader-action.dragging {
  background: #fff;
}
</style>